import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail, setAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist Bearer token for iOS Safari / cookie-blocked browsers.
      // Backend still sets HttpOnly cookies as the primary auth path.
      if (data && data.access_token) setAccessToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const raw = typeof detail === "string" ? detail : "";
      // Super-admin disabled account — surface the reason so the login page
      // can render a friendly "account disabled" banner instead of a toast.
      if (status === 403 && raw.toLowerCase().includes("disabled")) {
        // Backend returns "This account has been disabled by an administrator. Reason: …"
        const m = raw.match(/reason:\s*(.+)$/i);
        return {
          ok: false,
          status: 403,
          disabled: true,
          disabledReason: m ? m[1].trim() : null,
          error: raw,
        };
      }
      return { ok: false, status, error: formatApiErrorDetail(detail) || e.message };
    }
  };

  const register = async (email, password, name, extras = {}) => {
    try {
      const payload = { email, password, name, ...extras };
      const { data } = await api.post("/auth/register", payload);
      if (data && data.access_token) setAccessToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    setAccessToken(null);
    setUser(false);
  };

  const isSuperAdmin = !!(user && user.is_super_admin);
  const hasPracticeOpsAccess = !!(user && (user.practice_ops_access || user.is_super_admin));

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, isSuperAdmin, hasPracticeOpsAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
